import { Component, ContentChildren, OnInit, QueryList } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validator, Validators } from '@angular/forms';
import { PasswordStrength } from '@app/core/interfaces/password-strength';
import { PasswordStrengthRepository } from '@app/core/repositories/password-strength.repository';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { PasswordStrengthInputMode } from '@app/shared/models/password-strength-input-mode';
import { FieldsAreEqualValidator } from '@app/utils/form-mapping/helpers/fields-are-equal.validator';
import { FormHelper } from '@app/utils/forms';
import { loggerFor } from '@app/utils/logger';
import { ngValidatorAccessor } from '@app/utils/ng-validator-accessor';
import { ngValueAccessor } from '@app/utils/ng-value-accessor';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, timer } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';

interface PasswordStrengthForm {
  password: FormControl<string>;
  passwordConfirmation: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'portal-password-strength',
  templateUrl: './password-strength.component.html',
  styles: [],
  providers: [ngValueAccessor(PasswordStrengthComponent), ngValidatorAccessor(PasswordStrengthComponent)],
  standalone: false,
})
export class PasswordStrengthComponent extends ReactiveComponent implements OnInit, Validator {
  @ContentChildren(TranscludeDirective)
  transclusions: QueryList<TranscludeDirective>;

  checkingPassword!: boolean;
  form!: FormGroup<PasswordStrengthForm>;
  inputMode!: PasswordStrengthInputMode;
  loading!: boolean;
  passwordStrength?: PasswordStrength;
  strongPassword?: string;

  PasswordStrengthInputMode = PasswordStrengthInputMode;

  private logger = loggerFor('PasswordStrengthComponent');

  get longEnough(): boolean {
    return this.form.value.password?.length >= this.passwordStrength?.minimalLength;
  }

  get passwordType() {
    return this.inputMode === PasswordStrengthInputMode.AUTOGENERATE ? 'text' : 'password';
  }

  constructor(
    private fb: FormBuilder,
    private passwordStrengthRepository: PasswordStrengthRepository
  ) {
    super();
  }

  ngOnInit() {
    this.form = this.buildForm();
    this.inputMode = PasswordStrengthInputMode.AUTOGENERATE;
    this.loading = false;

    // Get password strength criteria from backend
    this.passwordStrengthRepository.checkStrength('').subscribe((passwordStrength) => {
      this.passwordStrength = passwordStrength;
    });

    this.updateFormValidator();
    this.updateStrongPassword();
  }

  emitPassword() {
    if (this.inputMode === PasswordStrengthInputMode.AUTOGENERATE) {
      // Emit reactive form value
      this.onChange(this.strongPassword);
      this.onTouched();
    } else {
      if (this.passwordStrength.passwordOk === true && this.form.valid) {
        this.logger.debug('Emitting password');

        // Emit reactive form value
        this.onChange(this.form.value.password);
      } else {
        this.logger.debug(
          'Password is not ok or does not match the confirmation password. Emitting undefined'
        );

        // Emit reactive form value
        this.onChange(undefined);
      }

      this.onValidatorChanged();
    }
  }

  reloadPassword() {
    this.inputMode = PasswordStrengthInputMode.AUTOGENERATE;

    this.updateFormValidator();
    this.updateStrongPassword();
  }

  setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);

    FormHelper.setEnabled(this.form.controls.password, isDisabled === false);
    FormHelper.setEnabled(this.form.controls.passwordConfirmation, isDisabled === false);
  }

  toggleMode() {
    if (this.inputMode === PasswordStrengthInputMode.AUTOGENERATE) {
      this.inputMode = PasswordStrengthInputMode.ENTER_PASSWORD;
      this.form.patchValue({
        password: '',
        passwordConfirmation: '',
      });

      this.form.controls.password.enable();
      this.form.markAsPristine();
    } else {
      this.inputMode = PasswordStrengthInputMode.AUTOGENERATE;
    }

    this.updateFormValidator();
    this.emitPassword();

    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.onValidatorChanged();
    });
  }

  validate(): ValidationErrors | null {
    return this.form.errors;
  }

  writeValue(obj: any): void {
    this.form.patchValue(
      {
        password: obj,
      },
      {
        emitEvent: false,
      }
    );
  }

  private buildForm(): FormGroup<PasswordStrengthForm> {
    return this.fb.group({
      password: [''],
      passwordConfirmation: [''],
    });
  }

  private updateFormValidator(): void {
    if (this.inputMode === PasswordStrengthInputMode.ENTER_PASSWORD) {
      this.form.setValidators(FieldsAreEqualValidator('password', 'passwordConfirmation'));
      this.form.get('password').setValidators(Validators.maxLength(64));
      this.form.get('password').setAsyncValidators((control) => this.validatePassword(control.value));
    } else {
      this.form.setValidators(null);
      this.form.get('password').setValidators(null);
      this.form.get('password').setAsyncValidators(null);
    }

    this.form.updateValueAndValidity();
  }

  private updateStrongPassword(): void {
    this.loading = true;

    this.passwordStrengthRepository
      .getStrongPassword()
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((password) => {
        this.strongPassword = password;

        if (this.inputMode === PasswordStrengthInputMode.AUTOGENERATE) {
          this.form.patchValue({
            password,
          });

          this.form.controls.password.disable();
          this.form.markAsDirty();

          this.emitPassword();
        }
      });
  }

  private validatePassword(password: string): Observable<ValidationErrors | null> {
    return timer(250).pipe(
      tap(() => (this.checkingPassword = true)),
      switchMap(() => {
        return this.passwordStrengthRepository.checkStrength(password).pipe(
          tap((passwordStrength) => {
            this.passwordStrength = passwordStrength;
          }),
          map((passwordStrength) => (passwordStrength.passwordOk ? null : {}))
        );
      }),
      tap(() => (this.checkingPassword = false)),
      untilDestroyed(this)
    );
  }
}
